/**
 * Authentication API types
 */
import type { GatewayId } from './types/third-party-system.enums';
import type { Role, Status } from './identity-contracts';
import { CredentialType } from './secret-contracts';
import type { PaymentRequestAccessTokenExecutionScopeByIpg } from './types/ipg/payment-request-access-token-execution-scope.registry';
import type {
    PermissionsBundlePublic,
    RoleAssignmentDetailed,
    RoleAssignmentPermissionContext,
} from './permission.types';
import type { CombinedConfigResponse } from './samba-config.types';
import type { OpaqueCursorPagination } from './authorization.types';
import type { OpaqueCursorSearchRequest } from './omnisearch.types';
import type { PasswordPolicyRejectionReason } from './password-policy.types';
import type { PaymentRequestStatus } from './payment.types';

/**
 * Login request
 */
export interface LoginRequest {
    email: string;
    password: string;
    payment_request_uuid?: string;
}

export interface PasswordlessLoginRequest {
    email: string;
    payment_request_uuid?: string;
}

export interface PasswordlessLoginChallengeResponse {
    message: string;
    expires_at: string;
}

export interface PasswordlessLoginCodeVerificationRequest {
    code: string;
}

export interface PasswordlessLoginLinkExchangeRequest {
    token: string;
}

export interface PasswordlessLoginDeepLinkToken {
    readonly token_kind: 'passwordless_login';
    readonly token: string;
}

export interface PillarLoginLinkDeepLinkToken {
    readonly token_kind: 'pillar_login_link';
    readonly token: string;
}

// Discriminator: token_kind
export type AnchorbaseLoginDeepLinkToken =
    | PasswordlessLoginDeepLinkToken
    | PillarLoginLinkDeepLinkToken;

export interface TotpSetupActivationTarget {
    role_assignment_uuid: string;
    organization_uuid: string | null;
    organization_name: string | null;
}

export interface MfaSetupStatusResponse {
    activation_target: TotpSetupActivationTarget | null;
    available_mfa_setup_methods: MfaMethod[];
}

/**
 * Login response
 * If requires_totp is true, user must verify an existing TOTP setup
 * before being fully authenticated.
 * If requires_totp_setup is true, the user is authenticated and the
 * requested role assignment is active, but TOTP setup must be completed
 * before continuing to use that role assignment.
 */
export interface LoginResponse {
    message: string;
    requires_totp: boolean;
    requires_mfa: boolean;
    available_mfa_methods: MfaMethod[];
    requires_mfa_setup: boolean;
    available_mfa_setup_methods: MfaMethod[];
    requires_totp_setup: boolean;
    requires_password_change: boolean;
    mfa_setup_activation_target?: TotpSetupActivationTarget;
    totp_setup_activation_target?: TotpSetupActivationTarget;
    user?: {
        user_uuid: string;
        primary_email: string;
        emails: string[];
        status: Status;
        totp_enabled: boolean;
    };
}

/**
 * Session-scoped profile returned by authApi.getProfile().
 *
 * Note: This shape intentionally does not expose the internal database pkey
 * `user_id`. Role assignments may still include pkey fields until the
 * corresponding endpoints are migrated.
 */
export interface SessionUserProfileDetailedResponse {
    user_uuid: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    emails: string[];
    phones?: string[];
    primary_phone?: string | null;
    totp_enabled: boolean;
    mfa_email_allowed?: boolean;
    mfa_sms_allowed?: boolean;
    status: Status;
    requires_password_change?: boolean;
    invitation_token?: string;
    default_role_assignment: RoleAssignmentDetailed | null;
    other_role_assignments: RoleAssignmentDetailed[];
}

export interface ActiveRoleAssignmentResponse extends RoleAssignmentPermissionContext {
    role_assignment_uuid: string;
    organization_uuid: string | null;
    permissions_bundle: PermissionsBundlePublic;
    combined_config: CombinedConfigResponse;
}

export interface CompleteCustomerPaymentLinkAttachmentRequest {
    payment_request_uuid: string;
}

/**
 * Verify TOTP request
 */
export interface VerifyTotpRequest {
    totp_code: string;
}

/**
 * Verify TOTP response
 */
export interface VerifyTotpResponse {
    user_uuid: string;
    primary_email: string;
    roles: string[];
}

export type MfaMethod = 'TOTP' | 'EMAIL' | 'SMS';

export interface MfaLoginChallengeResponse {
    message: string;
    delivery_destination: string;
    expires_at: string;
}

export interface VerifyMfaLoginRequest {
    code: string;
}

export type VerifyMfaLoginResponse = VerifyTotpResponse;

export interface MfaPolicyStatusResponse {
    required: boolean;
    totp_enabled: boolean;
    email_allowed: boolean;
    sms_allowed: boolean;
    available_login_methods: MfaMethod[];
    primary_email_available: boolean;
    primary_phone_available: boolean;
    can_disable: boolean;
}

export interface MfaActivationRequestResponse {
    message: string;
    delivery_destination: string;
    expires_at: string;
}

export interface VerifyMfaActivationRequest {
    code: string;
}

export interface EmailMfaActivationTokenPayload {
    user_id: number;
    email: string;
}

export interface SmsMfaActivationTokenPayload {
    user_id: number;
    phone: string;
}

export interface SmsVerificationTokenPayload {
    user_id: number;
    phone: string;
}

/**
 * Verify email request
 */
export interface VerifyEmailRequest {
    token: string;
    password?: string; // Required for PENDING users
}

/**
 * Verify email response
 */
export interface VerifyEmailResponse {
    message: string;
    user: {
        user_uuid: string;
        primary_email: string | null;
        status: Status;
    };
}

export interface CreateCustomerSelfOnboardingRequest {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    payment_request_uuid?: string;
    store_payment_method_for_future_use?: boolean;
}

export interface CreateCustomerSelfOnboardingResponse {
    message: string;
    primary_email: string;
    customer_organization_uuid: string;
}

export type CheckProposedPasswordRejectedReason =
    | PasswordPolicyRejectionReason
    | 'PASSWORD_TOO_LONG'
    | 'COMPROMISED_PASSWORD_CORPUS_MATCH'
    | 'COMPROMISED_PASSWORD_CORPUS_UNAVAILABLE';

export interface CheckProposedPasswordRequest {
    password: string;
}

export interface CheckProposedPasswordResponse {
    accepted: boolean;
    rejected_reasons: CheckProposedPasswordRejectedReason[];
}

export interface InvitationTokenPayload {
    primary_email: string;
    role: Role;
    organization_id: number | null;
    first_name: string | null;
    last_name: string | null;
    emails?: string[];
    phones?: string[];
    primary_phone?: string | null;
    policies?: string[];
    policy_bundle_ids?: number[];
}

export interface EmailVerificationTokenPayload {
    user_id: number;
    email: string;
}

export interface PaymentRequestAccessTokenPaymentConfigurationSnapshot {
    account_id: string;
    credential_uuid: string;
    gateway_id: GatewayId;
    credential_type: CredentialType;
    is_virtual: boolean | null;
    allow_surcharge: boolean;
    allow_convenience_fee: boolean;
    allow_override_fee: boolean;
    addressRequiredForCardPayment: boolean;
    surcharge_pct: number | null;
    convenience_fee: number | null;
}

export type PaymentRequestAccessTokenExecutionScope =
    PaymentRequestAccessTokenExecutionScopeByIpg[keyof PaymentRequestAccessTokenExecutionScopeByIpg];

export interface PaymentRequestAccessTokenPayload {
    payment_request_uuid: string;
    payment_execution_scope: PaymentRequestAccessTokenExecutionScope;
}

export interface PillarLoginLinkTokenPayload {
    active_role_assignment_id: number;
}

export type SingleUseAuthTokenType =
    | 'INVITATION'
    | 'PASSWORD_RESET'
    | 'EMAIL_VERIFICATION'
    | 'SMS_VERIFICATION'
    | 'PAYMENT_REQUEST_ACCESS'
    | 'PILLAR_LOGIN_LINK'
    | 'MFA_ENABLE_EMAIL'
    | 'MFA_ENABLE_SMS'
    | 'MFA_LOGIN_EMAIL'
    | 'MFA_LOGIN_SMS';

export type SingleUseAuthTokenPayloadByType = {
    readonly INVITATION: InvitationTokenPayload | null;
    readonly PASSWORD_RESET: null;
    readonly EMAIL_VERIFICATION: EmailVerificationTokenPayload;
    readonly SMS_VERIFICATION: SmsVerificationTokenPayload;
    readonly PAYMENT_REQUEST_ACCESS: PaymentRequestAccessTokenPayload;
    readonly PILLAR_LOGIN_LINK: PillarLoginLinkTokenPayload;
    readonly MFA_ENABLE_EMAIL: EmailMfaActivationTokenPayload;
    readonly MFA_ENABLE_SMS: SmsMfaActivationTokenPayload;
    readonly MFA_LOGIN_EMAIL: EmailMfaActivationTokenPayload;
    readonly MFA_LOGIN_SMS: SmsMfaActivationTokenPayload;
};

export type SingleUseAuthTokenPayload = SingleUseAuthTokenPayloadByType[SingleUseAuthTokenType];

export const SingleUseAuthTokenPayloadStorageVersion = {
    V2: 2,
} as const;

export type SingleUseAuthTokenPayloadStorageVersion =
    (typeof SingleUseAuthTokenPayloadStorageVersion)[keyof typeof SingleUseAuthTokenPayloadStorageVersion];

export type SingleUseAuthTokenPayloadStorageV1<Type extends SingleUseAuthTokenType> =
    SingleUseAuthTokenPayloadByType[Type];

export interface SingleUseAuthTokenPayloadStorageV2<Type extends SingleUseAuthTokenType> {
    readonly single_use_auth_token_payload_version: typeof SingleUseAuthTokenPayloadStorageVersion.V2;
    readonly payload: SingleUseAuthTokenPayloadByType[Type];
}

export interface SingleUseAuthTokenPayloadStorageEnvelope<
    Type extends SingleUseAuthTokenType = SingleUseAuthTokenType,
> {
    readonly type: Type;
    readonly payload:
        | SingleUseAuthTokenPayloadStorageV1<Type>
        | SingleUseAuthTokenPayloadStorageV2<Type>;
}

export interface SingleUseTokenPaymentRequestSummary {
    payment_request_uuid: string;
    merchant_organization_uuid: string;
    status: PaymentRequestStatus;
    customer_name?: string;
    recipient_email?: string;
    recipient_phone?: string;
    original_amount?: number;
    currency_code?: string;
}

export interface SingleUseTokenOrganizationSummary {
    organization_uuid: string;
    name: string;
}

export interface SingleUseTokenListItem {
    single_use_auth_token_uuid: string;
    type: SingleUseAuthTokenType;
    user_uuid: string | null;
    user_primary_email: string | null;
    email: string | null;
    organizations: SingleUseTokenOrganizationSummary[];
    payment_request?: SingleUseTokenPaymentRequestSummary;
    created_ts: string;
    expire_ts: string;
    used_ts: string | null;
    is_outstanding: boolean;
    is_expired: boolean;
}

export interface ListSingleUseTokensRequest {
    outstanding_only: boolean;
    q?: string;
    limit?: number;
    cursor?: string;
}

export interface SingleUseTokensSearchRequest extends OpaqueCursorSearchRequest {
    outstanding_only: boolean;
}

export interface ListSingleUseTokensResponse {
    tokens: SingleUseTokenListItem[];
    pagination: OpaqueCursorPagination;
}

export interface SwapCookieForTokenResponse {
    token: string;
    expires_at: string;
}

export interface NativeAppLoginLinkResponse {
    application_url: string;
    expires_at: string;
}

export interface CancelNativeAppLoginLinkRequest {
    application_url: string;
}

export interface SwapTokenForCookieRequest {
    token: string;
}

export type SwapTokenForCookieResponse = LoginResponse;

/**
 * Change password request
 */
export interface ChangePasswordRequest {
    old_password: string;
    new_password: string;
}

/**
 * Setup TOTP response
 */
export interface SetupTotpResponse {
    secret: string;
    qr_code: string;
    message: string;
}

/**
 * Enable TOTP request
 */
export interface EnableTotpRequest {
    secret: string;
    code: string;
}

/**
 * Generate backup codes response
 */
export interface GenerateBackupCodesResponse {
    codes: string[];
}

export interface SetDefaultRoleAssignmentUuidRequest {
    role_assignment_uuid: string;
}

/**
 * Set default role assignment request
 */
export type SetDefaultRoleAssignmentRequest = SetDefaultRoleAssignmentUuidRequest;

/**
 * Set active role assignment request
 */
export interface SetActiveRoleAssignmentRequest {
    role_assignment_id: number;
}

export interface SetActiveRoleAssignmentUuidRequest {
    role_assignment_uuid: string;
}

export interface SetActiveRoleAssignmentResponse {
    message: string;
    requires_totp_setup: boolean;
    totp_setup_activation_target?: TotpSetupActivationTarget;
}

export interface StartImpersonationRequest {
    role_assignment_uuid: string;
}

export interface ImpersonationContextResponse {
    impersonated_primary_email: string;
    impersonated_role_assignment_uuid: string;
    impersonated_role: string;
    impersonated_organization_name: string | null;
}

/**
 * Send verification email response
 */
export interface SendVerificationEmailResponse {
    message: string;
    verification_link: string;
}

/**
 * Request password reset request
 */
export interface RequestPasswordResetRequest {
    email: string;
}

/**
 * Request password reset response
 */
export interface RequestPasswordResetResponse {
    success: boolean;
    message: string;
    reset_link?: string; // For MVP manual distribution
}

export interface ValidatePasswordResetTokenRequest {
    token: string;
}

export interface ValidatePasswordResetTokenResponse {
    valid: boolean;
    message: string;
}

/**
 * Reset password request
 */
export interface ResetPasswordRequest {
    token: string;
    newPassword: string;
}

/**
 * Reset password response
 */
export interface ResetPasswordResponse {
    success: boolean;
    message: string;
}

/**
 * Verify and enable TOTP response
 */
export interface VerifyAndEnableTotpResponse {
    message: string;
    backup_codes: string[];
}

/**
 * Expire session admin response
 */
export interface ExpireSessionAdminResponse {
    message: string;
    user_email?: string;
}
